import { Navigate, Outlet } from "react-router-dom";
import { useAuth } from "@/lib/auth/useAuth";
import { useAuthStore } from "@/state/authStore";

/**
 * Full-screen spinner shown while Supabase resolves the current session.
 * Prevents the white flash / redirect-then-back that happens when the
 * auth state isn't known yet on first paint.
 */
function AppLoadingScreen() {
  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 border-2 border-zinc-700 border-t-[rgb(var(--cp))] rounded-full animate-spin" />
        <p className="text-zinc-500 text-sm">Loading…</p>
      </div>
    </div>
  );
}

/**
 * Protected Route Component
 * Redirects to /login if not authenticated
 */
export function ProtectedRoute() {
  const { isAuthenticated, isLoading } = useAuth();
  const hasHydrated = useAuthStore((s) => s._hasHydrated);

  if (!hasHydrated || isLoading) return <AppLoadingScreen />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <Outlet />;
}

/**
 * Subscription Required Route
 * Redirects to /billing if no active subscription
 */
export function SubscriptionRequired() {
  const { hasActiveSubscription, isLoading } = useAuth();
  const hasHydrated = useAuthStore((s) => s._hasHydrated);

  if (!hasHydrated || isLoading) return <AppLoadingScreen />;
  if (!hasActiveSubscription) return <Navigate to="/billing" replace />;
  return <Outlet />;
}

/**
 * Coach Route — /dev/coach
 * Requires login + email on the VITE_COACH_EMAILS allowlist.
 * Redirects unauthenticated users to /login; everyone else to /dashboard.
 * To add a coach: set VITE_COACH_EMAILS=email1@x.com,email2@x.com in .env.local / Netlify.
 */
// Computed once at module load — env vars are constants, no need to re-parse on every render.
const COACH_EMAILS: Set<string> = new Set(
  (import.meta.env.VITE_COACH_EMAILS as string | undefined)
    ?.split(",")
    .map((e) => e.trim().toLowerCase())
    .filter(Boolean) ?? [],
);

/** Returns true if the given email is on the VITE_COACH_EMAILS allowlist. */
export function isCoachEmail(email: string | undefined | null): boolean {
  if (!email) return false;
  return COACH_EMAILS.has(email.toLowerCase());
}

export function CoachRoute() {
  const { isAuthenticated, isLoading } = useAuth();
  const hasHydrated = useAuthStore((s) => s._hasHydrated);
  const account = useAuthStore((s) => s.account);

  if (!hasHydrated || isLoading) return <AppLoadingScreen />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;

  if (!isCoachEmail(account?.email)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Outlet />;
}

/**
 * Guest Only Route (Login, Signup)
 * Redirects authenticated users to /dashboard (if subscribed) or /billing (if not).
 */
export function GuestOnlyRoute() {
  const { isAuthenticated, isLoading, hasActiveSubscription } = useAuth();
  const hasHydrated = useAuthStore((s) => s._hasHydrated);

  if (!hasHydrated || isLoading) return <AppLoadingScreen />;
  if (isAuthenticated) {
    return <Navigate to={hasActiveSubscription ? "/dashboard" : "/billing"} replace />;
  }
  return <Outlet />;
}
